import { useState } from 'react'
import useDeleteMeeting from '../../modules/meeting/hooks/useDeleteMeeting'
import useResendFiles from '../../modules/meeting/hooks/useResendFiles'
import { Meeting } from '../../modules/meeting/model'
import { Button } from '../../onsaui'
import { formatTime } from '../../shared/helpers/time'
import ParticipantsList from '../participants/ParticipantsList'

const MeetingInfoPage: React.FC<{
  selectedMeeting: Meeting
  setSelectedMeeting: (meeting: Meeting | null) => void
  reloadMeetings: () => void
}> = ({ selectedMeeting, setSelectedMeeting, reloadMeetings }) => {
  const { isResending, resendFiles } = useResendFiles()
  const { isDeleting, deleteMeeting } = useDeleteMeeting()
  const [copiedSummary, setCopiedSummary] = useState(false)

  const handleCopySummary = async () => {
    if (!selectedMeeting.summary) return
    try {
      await navigator.clipboard.writeText(selectedMeeting.summary)
      setCopiedSummary(true)
      setTimeout(() => setCopiedSummary(false), 2000)
    } catch {
      // Fallback for environments without clipboard API
      const textarea = document.createElement('textarea')
      textarea.value = selectedMeeting.summary
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedSummary(true)
      setTimeout(() => setCopiedSummary(false), 2000)
    }
  }

  const handleSendMeetingFiles = async () => {
    if (!selectedMeeting) {
      console.error('No meeting selected')
      return
    }

    await resendFiles(selectedMeeting.id)
  }

  const handleDeleteMeeting = async () => {
    if (!selectedMeeting) {
      console.error('No meeting selected')
      return
    }

    await deleteMeeting(selectedMeeting.id)
    setSelectedMeeting(null)
    reloadMeetings()
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <Button
          size="small"
          variant="ghost"
          className="text-gray-600 hover:text-gray-500 focus:outline-none"
          onClick={() => setSelectedMeeting(null)}
        >
          ← Back
        </Button>
      </div>
      <h1 className="mb-2 text-2xl font-bold">
        {selectedMeeting.metadata?.title || 'Untitled Meeting'}
      </h1>
      <p className="mb-2 text-sm text-gray-600">
        Start Time: {formatTime(selectedMeeting.startTime)}
      </p>
      <p className="mb-2 text-sm text-gray-600">Meeting ID: {selectedMeeting.zoomId}</p>

      <ParticipantsList participants={selectedMeeting.participants || []} isLoading={false} />

      {selectedMeeting.summary && (
        <div className="mt-4 rounded-md bg-gray-50 p-4 shadow-sm">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-lg font-semibold">Summary</h3>
            <Button size="small" variant="ghost" onClick={handleCopySummary}>
              {copiedSummary ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
            {selectedMeeting.summary}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-col items-center text-center">
        <Button
          disabled={isResending}
          onClick={handleSendMeetingFiles}
          size="small"
          variant="outlined"
        >
          {isResending ? 'Resending...' : 'Resend Meeting Files'}
        </Button>
        <p className="mt-1 text-xs text-gray-400">
          The summary and transcript text files will be emailed to your address.
        </p>
      </div>

      {selectedMeeting.transcript && (
        <div className="mt-4 rounded-md bg-gray-50 p-4 shadow-sm">
          <h3 className="mb-2 text-lg font-semibold">Transcript</h3>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
            {selectedMeeting.transcript}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-col items-center text-center">
        <Button
          disabled={isDeleting}
          onClick={handleDeleteMeeting}
          className="border-red-400 text-red-500 hover:border-red-500 hover:bg-red-50 hover:text-red-600 active:bg-red-100 active:text-red-700"
          size="small"
          variant="outlined"
        >
          {isDeleting ? 'Deleting...' : 'Delete Meeting'}
        </Button>
        <p className="mt-1 text-xs text-gray-400">
          All data related to this meeting will be permanently deleted.
        </p>
      </div>
    </div>
  )
}

export default MeetingInfoPage
